import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { queryKeys } from '@/lib/queryKeys';
import type { WeatherForecast } from '@/lib/types';

/**
 * 내 기준 지점의 날씨.
 *
 * 서버가 진실이다(`GET /api/v1/home/weather`). 브라우저가 기상 API 를 직접 부르면 API 키가 번들에
 * 실리고, 그건 "프론트엔드는 자격증명을 갖지 않는다"는 v2 의 첫 번째 규칙을 어기는 것이다.
 *
 * **어느 지점인지는 여기서 정하지 않는다.** 요청에 지점을 실으면 서버가 그걸 믿어야 하고, 그러면
 * 우리 API 키로 아무 좌표나 조회시킬 수 있다. 서버가 로그인 사용자의 대표 소속에서 지점을 정하고,
 * 그 좌표의 **행정구역명**(예: `서울 성북구`)을 `locationName` 으로 돌려준다 — 위젯 머리글은 그 값을
 * 쓴다. 우리 내부 이름("본사", "판교 데이터센터")이 아니라 실제 지역명인 이유는, 그 자리가
 * "이 기온이 어디 값인가" 를 답하는 칸이기 때문이다.
 *
 * `useHrProfile` 과 달리 오프라인 데모 자료로 물러서지 않는다. 가짜 기온은 "오늘 작업이 되는가" 를
 * 잘못 답하고, 그 판단은 크레인을 세우거나 올리는 데 쓰인다 — 틀린 값보다 빈 위젯이 낫다.
 */

/** 날씨는 자주 바뀌지 않는다. 홈을 오갈 때마다 다시 부르지 않도록 넉넉히 잡는다(서버 캐시와 같은 10분). */
const STALE_TIME_MS = 10 * 60 * 1000;

const WEATHER_PATH = '/v1/home/weather';

export function useWeather(): UseQueryResult<WeatherForecast> {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: [...queryKeys.weather, userId],
    queryFn: () => apiFetch<WeatherForecast>(WEATHER_PATH),
    enabled: !!userId,
    staleTime: STALE_TIME_MS,
  });
}
